/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.0.0
 */
import { Fragment, useEffect, useState } from 'react';

import { Field, Formik, FormikHelpers } from 'formik';
import get from 'lodash/get';

import { LinkBlock } from '@components/common';
import { IOptionItemProps } from '@components/common/Select/Select';
import GTMOperations from '@modules/analytics/GoogleTagManager/GTMOperations';
import { ICommonTranslation } from '@modules/internationalization/types';
import { Loader } from '@modules/loader/components/Loader';
import ButtonServiceUserDenied from '@modules/role/components/ButtonServiceUserDenied';

import { getCustomAttrs, getInitValue } from '../../createInquiryUtils';
import { attrFieldValidation, emailValidation, fieldValidation, phoneValidation } from '../../createInquiryValidation';
import { IModalInquiryAttachFileActionPayload } from '../../types/createInquiryActionTypes';
import {
    ETopicAttrDataType,
    ETopicAttrVisibility,
    ETopicType,
    IInquiryAttachProps,
    IInquiryUser,
    ITopicCustomerAttr,
    ITopicInfo,
} from '../../types/createInquiryTypes';
import { CreateInquiryFooterStyled, CreateInquiryFootnoteStyled } from '../styled/CreateInquiryStyled';
import { CreateInquiryFormAttach } from './CreateInquiryFormAttach';
import { CreateInquiryFormCheckbox } from './CreateInquiryFormCheckbox';
import { CreateInquiryFormDate } from './CreateInquiryFormDate';
import { CreateInquiryFormInput } from './CreateInquiryFormInput';
import { CreateInquiryFormSelect } from './CreateInquiryFormSelect';
import { CreateInquiryFormTextArea } from './CreateInquiryFormTextArea';
import { CreateInquiryFieldContainer, CreateInquiryFormContainerStyled } from './styled/CreateInquiryFormStyled';

export interface ICreateInquiryFormProps {
    attachFile: (p: IModalInquiryAttachFileActionPayload) => void;
    attachList: IInquiryAttachProps[];
    closeModal: () => void;
    dettachFile: (p: number) => void;
    isLoading: boolean;
    onCreate: (values: IFormValues, attrs: ITopicCustomerAttr[]) => void;
    onSelectTopic: (id: string) => void;
    topicInfo: ITopicInfo;
    topicList: IOptionItemProps[];
    translation: ICommonTranslation;
    userInfo: IInquiryUser;
    type: ETopicType;
}

export interface IFormValues {
    [index: string]: any;
}

const CreateInquiryForm = (props: ICreateInquiryFormProps) => {
    const { createInquiry: inquiryTranslation } = props.translation;

    useEffect(() => {
        if (props.topicInfo.id) {
            const customAttrs = getCustomAttrs(props.topicInfo.customAttrs, props.topicInfo.dictionaries);
            const newFormValues: IFormValues = {
                topic: props.topicInfo.id,
            };

            newFormValues['description'] = '';
            newFormValues['email'] = props.userInfo.email;
            customAttrs.forEach((attr: ITopicCustomerAttr) => {
                newFormValues[attr.key] = getInitValue(attr);
            });

            setCustomAttrs(customAttrs);
            setTopicInfoLoad(true);
            setFormValues(newFormValues);
        }
    }, [props.topicInfo]);

    const [formValues, setFormValues] = useState<IFormValues>({ topic: props.topicInfo.id });
    const [customAttrs, setCustomAttrs] = useState<ITopicCustomerAttr[]>([]);
    const [topicInfoLoaded, setTopicInfoLoad] = useState(false);

    const selectTopicHandler = (selected: IOptionItemProps) => {
        const value = selected?.value
            ? typeof selected.value === 'string'
                ? selected.value
                : selected.value.toString()
            : '';
        props.onSelectTopic(value);
    };

    const submitHandler = (values: IFormValues, actions: FormikHelpers<IFormValues>) => {
        GTMOperations.navigation.inquiryCreateClickOperation();
        actions.setSubmitting(false);
        props.onCreate(values, customAttrs);
    };

    return (
        <CreateInquiryFormContainerStyled>
            <Formik enableReinitialize={true} initialValues={formValues} onSubmit={submitHandler}>
                {({ handleSubmit, isValid }) => (
                    <form onSubmit={handleSubmit}>
                        <CreateInquiryFieldContainer>
                            <Field
                                component={CreateInquiryFormSelect}
                                key="topic"
                                label={inquiryTranslation.topic}
                                name="topic"
                                options={props.topicList}
                                placeholder={inquiryTranslation.themePlaceholder}
                                required={true}
                                validate={(value: string) => fieldValidation(value, inquiryTranslation)}
                                onSelect={selectTopicHandler}
                            />
                            {topicInfoLoaded && (
                                <Field
                                    component={CreateInquiryFormTextArea}
                                    key="description"
                                    label={inquiryTranslation.description}
                                    name="description"
                                    placeholder={inquiryTranslation.descriptionPlaceholder}
                                    required={true}
                                    validate={(value: string) => fieldValidation(value, inquiryTranslation)}
                                />
                            )}
                            {topicInfoLoaded && (
                                <Fragment>
                                    <Field
                                        component={CreateInquiryFormInput}
                                        key="phone"
                                        label={inquiryTranslation.phone}
                                        name="phone"
                                        placeholder={inquiryTranslation.phonePlaceholder}
                                        tooltip={inquiryTranslation.phoneTooltip}
                                        validate={(value: string) => phoneValidation(value, inquiryTranslation)}
                                    />
                                    <Field
                                        component={CreateInquiryFormInput}
                                        key="email"
                                        label={inquiryTranslation.email}
                                        name="email"
                                        placeholder={inquiryTranslation.emailPlaceholder}
                                        required={true}
                                        tooltip={inquiryTranslation.emailTooltip}
                                        validate={(value: string) =>
                                            emailValidation(
                                                value,
                                                inquiryTranslation,
                                                get(props, 'userInfo.emailCheckPattern'),
                                            )
                                        }
                                    />
                                </Fragment>
                            )}
                            {topicInfoLoaded &&
                                customAttrs.map((attr: ITopicCustomerAttr) => {
                                    switch (attr.declaration.dataType) {
                                        case ETopicAttrDataType.BOOL:
                                            return (
                                                <Field
                                                    component={CreateInquiryFormCheckbox}
                                                    key={attr.key}
                                                    label={attr.name}
                                                    name={attr.key}
                                                    required={
                                                        attr.declaration.visibility !== ETopicAttrVisibility.MANDATORY
                                                    }
                                                    validate={(v: string) =>
                                                        attrFieldValidation(v, inquiryTranslation, attr)
                                                    }
                                                    visibility={attr.declaration.visibility}
                                                />
                                            );
                                        case ETopicAttrDataType.DICTIONARY:
                                            return (
                                                <Field
                                                    component={CreateInquiryFormSelect}
                                                    key={attr.key}
                                                    label={attr.name}
                                                    name={attr.key}
                                                    options={attr.optionsList}
                                                    placeholder={inquiryTranslation.notSelected}
                                                    required={
                                                        attr.declaration.visibility !== ETopicAttrVisibility.MANDATORY
                                                    }
                                                    validate={(v: string) =>
                                                        attrFieldValidation(v, inquiryTranslation, attr)
                                                    }
                                                    visibility={attr.declaration.visibility}
                                                />
                                            );
                                        case ETopicAttrDataType.DATE:
                                            return (
                                                <Field
                                                    component={CreateInquiryFormDate}
                                                    key={attr.key}
                                                    label={attr.name}
                                                    name={attr.key}
                                                    required={
                                                        attr.declaration.visibility !== ETopicAttrVisibility.MANDATORY
                                                    }
                                                    translation={props.translation}
                                                    validate={(v: string) =>
                                                        attrFieldValidation(v, inquiryTranslation, attr)
                                                    }
                                                    visibility={attr.declaration.visibility}
                                                />
                                            );
                                        default:
                                            return (
                                                <Field
                                                    component={CreateInquiryFormInput}
                                                    key={attr.key}
                                                    label={attr.name}
                                                    name={attr.key}
                                                    placeholder={attr.name}
                                                    required={
                                                        attr.declaration.visibility !== ETopicAttrVisibility.MANDATORY
                                                    }
                                                    validate={(v: string) =>
                                                        attrFieldValidation(v, inquiryTranslation, attr)
                                                    }
                                                    visibility={attr.declaration.visibility}
                                                />
                                            );
                                    }
                                })}
                            {topicInfoLoaded && (
                                <CreateInquiryFormAttach
                                    attachFile={props.attachFile}
                                    attachList={props.attachList}
                                    dettachFile={props.dettachFile}
                                    translation={inquiryTranslation}
                                    type={props.type}
                                />
                            )}
                            <CreateInquiryFootnoteStyled>
                                - {inquiryTranslation.inquiryFootnote}
                            </CreateInquiryFootnoteStyled>
                        </CreateInquiryFieldContainer>
                        <CreateInquiryFooterStyled>
                            <ButtonServiceUserDenied disabled={!isValid} kind="mediumNormal" type="submit">
                                {inquiryTranslation.create}
                            </ButtonServiceUserDenied>
                            <LinkBlock className="create-inquiry__footer-link" onClick={props.closeModal}>
                                {inquiryTranslation.close}
                            </LinkBlock>
                        </CreateInquiryFooterStyled>
                    </form>
                )}
            </Formik>
            {props.isLoading && <Loader showLoader={props.isLoading} />}
        </CreateInquiryFormContainerStyled>
    );
};
export default CreateInquiryForm;
